import { Command as CommandPrimitive } from 'cmdk';
import { Search, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { Button } from '../button';
import { DialogTitle } from '../dialog';
import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { DialogDrawer, DialogDrawerContent } from '../mobile-responsive-dialog-drawer';
import {
  commandDialogBannerCommandClassesExperimental,
  commandDialogCommandClassesExperimental,
  commandDialogContentClassesExperimental,
  commandGroupClassesExperimental,
  commandInputClassesExperimental,
  commandItemClassesExperimental,
  commandSeparatorClassesExperimental,
} from './command.experimental.styles';
import { createNfcAwareFilter } from './command.filters';
import {
  commandClasses,
  commandDialogBannerClasses,
  commandDialogBannerCommandClasses,
  commandDialogCommandClasses,
  commandDialogContentClasses,
  commandEmptyClasses,
  commandGroupClasses,
  commandInputClasses,
  commandInputClearClasses,
  commandInputIconClasses,
  commandInputWrapperClasses,
  commandItemClasses,
  commandListClasses,
  commandSeparatorClasses,
  commandShortcutClasses,
} from './command.styles';
import type {
  CommandDialogProps,
  CommandEmptyProps,
  CommandGroupProps,
  CommandInputProps,
  CommandItemProps,
  CommandListProps,
  CommandProps,
  CommandSeparatorProps,
  CommandShortcutProps,
} from './command.types';

const Command = React.forwardRef<React.ElementRef<typeof CommandPrimitive>, CommandProps>(
  ({ className, filter, ...props }, ref) => {
    const nfcAwareFilter = React.useMemo(createNfcAwareFilter, []);
    return (
      <CommandPrimitive
        loop
        ref={ref}
        className={cn(commandClasses, className)}
        filter={filter ?? nfcAwareFilter}
        {...props}
      />
    );
  }
);
Command.displayName = CommandPrimitive.displayName;

const CommandDialog = ({
  children,
  className,
  contentClassName,
  hideCloseIcon,
  onKeyDown,
  dismissible = true,
  value,
  onValueChange,
  banner,
  ...props
}: CommandDialogProps) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <DialogDrawer {...props}>
      <DialogTitle className="hidden"></DialogTitle>
      <DialogDrawerContent
        dismissible={dismissible}
        className={cn(
          isExperimentalForms
            ? commandDialogContentClassesExperimental
            : commandDialogContentClasses,
          banner != null && commandDialogBannerClasses,
          contentClassName
        )}
        hideCloseIcon={hideCloseIcon}
      >
        <Command
          data-experimental={isExperimentalForms || undefined}
          className={cn(
            isExperimentalForms
              ? commandDialogCommandClassesExperimental
              : commandDialogCommandClasses,
            // The banner makes the content `sm:overflow-visible`, so the Command
            // re-supplies the radius the clip used to give it.
            banner != null &&
              (isExperimentalForms
                ? commandDialogBannerCommandClassesExperimental
                : commandDialogBannerCommandClasses),
            className
          )}
          shouldFilter={false}
          onKeyDown={onKeyDown}
          value={value}
          onValueChange={onValueChange}
        >
          {children}
        </Command>
        {banner}
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  CommandInputProps
>(({ className, wrapperClassName, showClearButton, clearAriaLabel, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();

  const innerRef = React.useRef<HTMLInputElement | null>(null);
  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    [ref]
  );

  // Clear the query and return focus to the input. Keep the refocus: the button
  // unmounts the instant the value empties, so without it focus falls to <body>
  // and cmdk's arrow-key navigation is stranded.
  const handleClear = () => {
    props.onValueChange?.('');
    innerRef.current?.focus();
  };

  return (
    <div
      className={cn(commandInputWrapperClasses, wrapperClassName)}
      cmdk-input-wrapper=""
    >
      <Search
        className={commandInputIconClasses(Boolean(props.value) && !props.disabled)}
        aria-hidden="true"
      />
      <CommandPrimitive.Input
        ref={setRefs}
        data-experimental={isExperimentalForms || undefined}
        className={cn(
          isExperimentalForms ? commandInputClassesExperimental : commandInputClasses,
          className
        )}
        {...props}
      />
      {showClearButton && props.value && !props.disabled && (
        // `xxs` is deprecated and its 24px target is under the WCAG 2.5.8 floor, but
        // it keeps the clear in step with the other search fields' — a trade-off held
        // until the whole family moves to `xs` together.
        <Button
          type="button"
          variant="ghost"
          size="xxs"
          iconOnly
          flush="end"
          aria-label={clearAriaLabel ?? 'Clear search'}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleClear();
          }}
          onKeyDown={(e) => {
            // The button lives inside cmdk's root keydown scope, which
            // preventDefaults Enter/Space and selects the active item —
            // swallowing the button's own activation. Handle it here and stop
            // the event before it reaches the root.
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              handleClear();
            }
          }}
          className={commandInputClearClasses}
        >
          <X aria-hidden="true" />
        </Button>
      )}
    </div>
  );
});

CommandInput.displayName = CommandPrimitive.Input.displayName;

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  CommandListProps
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn(commandListClasses, className)}
    {...props}
  />
));

CommandList.displayName = CommandPrimitive.List.displayName;

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  CommandEmptyProps
>((props, ref) => (
  <CommandPrimitive.Empty ref={ref} className={commandEmptyClasses} {...props} />
));

CommandEmpty.displayName = CommandPrimitive.Empty.displayName;

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  CommandGroupProps
>(({ className, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <CommandPrimitive.Group
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms ? commandGroupClassesExperimental : commandGroupClasses,
        className
      )}
      {...props}
    />
  );
});

CommandGroup.displayName = CommandPrimitive.Group.displayName;

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  CommandSeparatorProps
>(({ className, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <CommandPrimitive.Separator
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? commandSeparatorClassesExperimental
          : commandSeparatorClasses,
        className
      )}
      {...props}
    />
  );
});
CommandSeparator.displayName = CommandPrimitive.Separator.displayName;

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  CommandItemProps
>(({ className, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <CommandPrimitive.Item
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms ? commandItemClassesExperimental : commandItemClasses,
        className
      )}
      {...props}
    />
  );
});

CommandItem.displayName = CommandPrimitive.Item.displayName;

const CommandShortcut = ({ className, ...props }: CommandShortcutProps) => {
  return <span className={cn(commandShortcutClasses, className)} {...props} />;
};
CommandShortcut.displayName = 'CommandShortcut';

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
};
